/* eslint-disable @nx/enforce-module-boundaries */
import { subMonths, formatISO } from 'date-fns'
import { useMutation } from 'react-query'
import { useState, useCallback } from 'react'
import { useToasts } from 'react-toast-notifications'

import { Button } from '@sweetspot/scramble-ds/atoms'
import {
  DataTable,
  DataTableFilters,
  Section,
  SectionContent,
  SectionHeader,
  SectionTitle,
} from '@sweetspot/scramble-ds'
import { useTranslation } from 'react-i18next'
import { columns } from './columns'
import { failedPaymentsFilters } from '../filters'
import { useFailedPayments } from '@sweetspot/club-portal/feature/payment'
import { _lockUnlockMemberships } from '@sweetspot/club-portal-legacy/services/membershipApi'
import PlayerCardModal from '@sweetspot/club-portal-legacy/modals/PlayerCard'
import {
  getFirstMatchingRole,
  hasAccess,
} from '@sweetspot/sweetspot-js/features/userAccess/utils/utils'
import { ACCESS_KEYS } from '@sweetspot/sweetspot-js/features/userAccess/constants/accessTable'
import { RootState } from '@sweetspot/club-portal-legacy/store/types'
import ConfirmModal from '@sweetspot/club-portal-legacy/components/ConfirmModal'
import { useSelector } from 'react-redux'
import { Payment } from '@sweetspot/shared/data-access/api-platform'

export const SubscriptionsFailedPaymentsTable = () => {
  const { t } = useTranslation()
  const { addToast } = useToasts()

  const [activeFilters, setActiveFilters] = useState<string[]>([])
  const [showPlayerCard, setShowPlayerCard] = useState(false)
  const [playerId, setPlayerId] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [selectedRows, setSelectedRows] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const lockMultipleMemberships = useMutation((selectedSubscriptionsFailedPaymentsTable) =>
    _lockUnlockMemberships(selectedSubscriptionsFailedPaymentsTable)
  )

  const NINE_MONTHS_AGO = formatISO(subMonths(new Date(), 9))
  const role = useSelector((state: RootState) => getFirstMatchingRole(state.auth.roles))

  const { data, refetch } = useFailedPayments({
    query: {
      'retry.attempts': activeFilters,
      page: currentPage,
      'retry.last_time[strictly_after]': NINE_MONTHS_AGO,
      limit: 50,
    },
    queryOptions: {
      keepPreviousData: true,
    },
  })

  const handleSelect = (selectedFailedPayments: Payment[]) => {
    const copySelectedFailedPayments = [...selectedFailedPayments]
    const noDuplicates = copySelectedFailedPayments.reduce((container: string[], failedPayment) => {
      if (
        failedPayment?.membership_card &&
        !container.includes(failedPayment?.membership_card?.uuid)
      ) {
        container.push(failedPayment?.membership_card?.uuid)
      }

      return container
    }, [])

    setSelectedRows(noDuplicates)
  }

  const handleLockMultipleMemberships = useCallback(
    (selectedFailedPayments: Payment[]) => {
      setIsLoading(true)

      lockMultipleMemberships.mutate(selectedFailedPayments, {
        onSuccess: () => {
          addToast(t('membershipLocked'), {
            appearance: 'success',
          })
          refetch()
        },
        onError: () => {
          addToast(t('toast.defaultError'), { appearance: 'error' })
        },
        onSettled: () => {
          setIsLoading(false)
          refetch()
        },
      })
    },
    [lockMultipleMemberships, addToast, t, refetch]
  )

  const renderLockMembershipsModalContent = () => {
    return <div className="mb-8 text-base">{t('membership.lockMultipleMembershipsModal')}</div>
  }

  const confirmLockMemberships = () => {
    handleLockMultipleMemberships(selectedRows)
    setShowModal(false)
  }

  const onFilterChange = (filter: string) => {
    if (!filter) {
      setActiveFilters([])
    } else if (activeFilters.includes(filter)) {
      const current = activeFilters.filter((item) => item !== filter)
      setActiveFilters(current)
    } else {
      setActiveFilters((prev) => [...prev, filter])
    }
    setCurrentPage(1)
  }

  const handleRowClick = (rowId: string | number) => {
    const failedPayment = data['hydra:member'].find((item) => item.id === rowId)
    if (!failedPayment?.player) return

    const {
      player: { id },
    } = failedPayment

    setPlayerId(id)
    setShowPlayerCard(true)
  }

  if (isLoading) return null

  return (
    <Section className="overflow-x-hidden">
      <SectionHeader>
        <SectionTitle>{t('transactions.header')}</SectionTitle>
      </SectionHeader>
      <SectionContent>
        <DataTable
          columns={columns(t, handleSelect)}
          data={data['hydra:member']}
          onRowClick={handleRowClick}
          extraControls={(table) => (
            <div className="flex flex-row py-7">
              <DataTableFilters
                table={table}
                filters={failedPaymentsFilters}
                onFilterChange={onFilterChange}
                activeFilters={activeFilters}
              />
              {hasAccess(ACCESS_KEYS.PAGES.PLAYERS.EXPORT, role?.value) && (
                <Button
                  size="small"
                  variant="ghost-dark"
                  disabled={selectedRows?.length === 0}
                  onClick={() => setShowModal(true)}
                  className="ml-2"
                >
                  {t('membership.lockMemberships')}
                  <i className="fa-regular fa-lock text-lg" />
                </Button>
              )}
            </div>
          )}
        />
      </SectionContent>
      {showModal && (
        <ConfirmModal
          headerText="membership.lockMemberships"
          children={renderLockMembershipsModalContent()}
          cancelText="cancel"
          confirmText="confirm"
          onCancel={() => setShowModal(false)}
          onConfirm={confirmLockMemberships}
        />
      )}
      {showPlayerCard && playerId && (
        <PlayerCardModal playerId={playerId} onClose={() => setShowPlayerCard(false)} />
      )}
    </Section>
  )
}
